// csvExport.js
export const exportTableToCSV = (
  tableData,
  columns,
  filename = "export.csv"
) => {
  // Get headers
  const headers = columns.map((col) => {
    const headerContent =
      typeof col.header === "function"
        ? col.header()?.props?.children
        : col.header;
    return headerContent || col.id;
  });

  // Convert data to CSV rows
  const rows = tableData.map((row) => {
    return columns.map((col) => {
      let value;

      // For expenses (including EBITDAR)
      if (col.id.startsWith("expenses.")) {
        const parts = col.id.split(".");
        const type = parts[1];
        const suffix = parts[2]?.toLowerCase();
        const item = row.expenses?.find((item) => item.Type === type);

        if (suffix === "annual") {
          value = item?.Annual;
        } else if (suffix === "ppd") {
          value = item?.PPD;
        } else if (suffix === "percentages") {
          value = item?.Percentages;
        } else if (suffix === "margin") {
          value = item?.PPD; // EBITDAR margin uses PPD property
        } else {
          value = item?.Annual; // fallback
        }
      }
      // For revenue
      else if (col.id.startsWith("revenue.")) {
        const parts = col.id.split(".");
        const type = parts[1];
        const suffix = parts[2]?.toLowerCase();
        const item = row.revenue?.find((item) => item.Type === type);

        if (suffix === "annual") {
          value = item?.Annual;
        } else if (suffix === "ppd") {
          value = item?.PPD;
        } else if (suffix === "percentages") {
          value = item?.Percentages;
        } else {
          value = item?.Annual; // fallback
        }
      }
      // For occupancy
      else if (col.id.startsWith("occupancy.")) {
        const parts = col.id.split(".");
        const type = parts[1];
        const suffix = parts[2]?.toLowerCase();
        const item = row.occupancy?.find((item) => item.Type === type);

        if (suffix === "annual") {
          value = item?.Annual;
        } else if (suffix === "adc") {
          value = item?.PPD;
        } else if (suffix === "percentages") {
          value = item?.Percentages;
        } else {
          value = item?.PPD || item?.Percentages; // fallback
        }
      }
      // For HUD Debt
      else if (col.id.startsWith("hudDebt.")) {
        const field = col.id.split(".")[1];
        const hudDebtData = row.hudDebt?.["HUD Debt"] || [];
        const item = hudDebtData.find((item) => item.Type === field);

        if (field === "Interest Rate") {
          value = item?.Rate;
        } else if (field === "Endorsement Date") {
          value = item?.Date;
        } else {
          value = item?.Amount;
        }
      }
      // For facility details
      else if (col.id.startsWith("details.")) {
        const field = col.id.split(".")[1];
        value = row.details?.[field];
      }

      // Handle null/undefined
      if (value === null || value === undefined) {
        return '""';
      }

      // Format numbers with explicit decimal places
      if (typeof value === "number") {
        value = value.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          useGrouping: false,
        });
        return `"${value}"`;
      }

      // Handle strings with special characters
      value = value.toString();
      if (value.includes(",") || value.includes('"') || value.includes("\n")) {
        value = value.replace(/"/g, '""');
      }
      return `"${value}"`;
    });
  });

  // Create CSV content with BOM for Excel
  const csvContent =
    "\ufeff" +
    [
      headers.map((h) => `"${h}"`).join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\r\n");

  // Create blob with Excel-friendly MIME type
  const blob = new Blob([csvContent], {
    type: "application/vnd.ms-excel;charset=utf-8",
  });

  // Create download link
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// New CSV export function specifically for finance tables
export const exportFinanceTableToCSV = (
  tableData,
  columns,
  currentFacility,
  selectedPeriods,
  filename = "finance_export.csv"
) => {
  // Flatten the grouped columns to get individual column headers
  const flattenedColumns = [];

  columns.forEach((col) => {
    if (col.columns) {
      // This is a grouped column (period group)
      col.columns.forEach((subCol) => {
        flattenedColumns.push({
          id: subCol.id,
          header: `${col.header} - ${
            subCol.header()?.props?.children || subCol.id
          }`,
          accessor: subCol.accessorFn,
        });
      });
    } else {
      // This is a regular column
      flattenedColumns.push({
        id: col.id,
        header: col.header()?.props?.children || col.id,
        accessor: col.accessorFn,
      });
    }
  });

  // Get headers
  const headers = flattenedColumns.map((col) => col.header);

  // Convert data to CSV rows
  const rows = tableData.map((row) => {
    return flattenedColumns.map((col) => {
      let value;

      if (col.accessor && typeof col.accessor === "function") {
        // Use the accessor function to get the value
        value = col.accessor(row);
      } else if (col.id === "type") {
        // Handle the type column
        value = row.Type;
      } else {
        // Handle period-based columns by parsing the column ID
        const parts = col.id.split("-");
        if (parts.length >= 2) {
          const period = parts[0];
          const dataType = parts[1]; // 'annual', 'ppd', 'percentages'

          // Get the data for this period from the facility's periods
          let periodData = [];

          // Determine which category to look in based on the table type
          // We can infer this from the data structure or column context
          if (row.Type) {
            // Try Revenue first
            const revenueData =
              currentFacility?.periods?.[period]?.Categories?.Revenue || [];
            const revenueMatch = revenueData.find(
              (item) => item.Type === row.Type
            );

            if (revenueMatch) {
              periodData = revenueData;
            } else {
              // Try Expenses
              const expenseData =
                currentFacility?.periods?.[period]?.Categories?.Expenses || [];
              periodData = expenseData;
            }
          }

          const matchingItem = periodData.find(
            (item) => item.Type === row.Type
          );

          if (dataType === "annual") {
            value = matchingItem?.Annual;
          } else if (dataType === "ppd") {
            value = matchingItem?.PPD;
          } else if (dataType === "percentages") {
            value = matchingItem?.Percentages;
          }
        }
      }

      // Handle special cases for complex values (like the expense table's PPD/Percentages column)
      if (value && typeof value === "object") {
        if (value.value !== undefined) {
          value = value.value;
        } else if (value.isPPD !== undefined) {
          // This is from the expense table's combined PPD/Percentages column
          value = value.value;
        }
      }

      // Handle null/undefined
      if (value === null || value === undefined || value === "") {
        return "";
      }

      // Format numbers
      if (typeof value === "number") {
        // Don't add quotes around numbers, let Excel handle the formatting
        return value.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          useGrouping: false,
        });
      }

      // Handle strings - escape quotes and wrap in quotes if needed
      value = value.toString();
      if (value.includes(",") || value.includes('"') || value.includes("\n")) {
        value = value.replace(/"/g, '""');
        return `"${value}"`;
      }

      // For percentage strings, don't wrap in quotes
      if (value.includes("%")) {
        return value;
      }

      return value;
    });
  });

  // Create CSV content with BOM for Excel
  const csvContent =
    "\ufeff" +
    [
      headers.map((h) => `"${h}"`).join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\r\n");

  // Create blob with Excel-friendly MIME type
  const blob = new Blob([csvContent], {
    type: "application/vnd.ms-excel;charset=utf-8",
  });

  // Create download link
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// CSV export function specifically for competition tables
export const exportCompetitionTableToCSV = (
  tableData,
  columns,
  filename = "competition_export.csv"
) => {
  // Get headers from the column definitions
  const headers = columns.map((col) => {
    const headerContent =
      typeof col.header === "function"
        ? col.header()?.props?.children
        : col.header;
    return headerContent || col.id;
  });

  // Convert data to CSV rows
  const rows = tableData.map((row) => {
    return columns.map((col) => {
      let value;

      // Use the column's accessor function to get the value
      if (col.accessorFn && typeof col.accessorFn === "function") {
        value = col.accessorFn(row);
      } else if (col.accessorKey) {
        value = row[col.accessorKey];
      } else {
        // Fallback - try to get value by column id
        value = row[col.id];
      }

      // Handle React components (like star ratings) - extract the underlying value
      if (value && typeof value === "object" && value.props) {
        // For star ratings, extract the value prop
        if (value.props.value !== undefined) {
          value = value.props.value;
        } else {
          // For other React components, try to extract text content
          value = extractTextFromReactElement(value);
        }
      }

      // Handle percentile progress bars - extract the percentage
      if (typeof value === "object" && value?.props?.children) {
        const progressBar = value.props.children;
        if (Array.isArray(progressBar)) {
          // Look for percentage text in the progress bar structure
          const percentElement = progressBar.find((child) =>
            child?.props?.children?.props?.children?.includes?.("%")
          );
          if (percentElement) {
            value = percentElement.props.children.props.children;
          }
        }
      }

      // Handle null/undefined
      if (value === null || value === undefined || value === "") {
        return "";
      }

      // Format numbers
      if (typeof value === "number") {
        return value.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          useGrouping: false,
        });
      }

      // Handle strings - escape quotes and wrap in quotes if needed
      value = value.toString();
      if (value.includes(",") || value.includes('"') || value.includes("\n")) {
        value = value.replace(/"/g, '""');
        return `"${value}"`;
      }

      return value;
    });
  });

  // Create CSV content with BOM for Excel
  const csvContent =
    "\ufeff" +
    [
      headers.map((h) => `"${h}"`).join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\r\n");

  // Create blob with Excel-friendly MIME type
  const blob = new Blob([csvContent], {
    type: "application/vnd.ms-excel;charset=utf-8",
  });

  // Create download link
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Helper function to extract text content from React elements
const extractTextFromReactElement = (element) => {
  if (typeof element === "string" || typeof element === "number") {
    return element;
  }

  if (element?.props?.children) {
    if (Array.isArray(element.props.children)) {
      return element.props.children.map(extractTextFromReactElement).join(" ");
    }
    return extractTextFromReactElement(element.props.children);
  }

  return "";
};
